import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IApiQueryOptions } from 'api/ApiClient';
import { AdminSitesList, Input, InputIconWrapper, InputLoader, PageHeader, PageTitle, PaginatedList, Section, SectionHeader } from 'components';
import { getQueryStringValue, useDebounce, useQueryString } from 'hooks';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { Employers, getEmployerSitesRequest } from 'store/Employers';
import { IStore } from 'store/types';
import { selectCurrentUser } from 'store/User';
import { ApiQueryOperator, IApiList, ISite } from 'types';

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const { organizationId: currentOrganizationId } = useSelector(selectCurrentEmployee());
  const { firstName } = useSelector(selectCurrentUser());
  const [dashboardSitesResponse, setDashboardSitesResponse] = useState<IApiList<ISite>>();
  const dashboardSites = dashboardSitesResponse?.data;
  const totalSites = dashboardSitesResponse?.total || 0;
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employers.GET_EMPLOYER_SITES_REQUEST));

  const [urlQuery, setUrlQuery] = useQueryString('adminDashboardSites', window.location, { limit: 5, offset: 0 });
  const defaultSearchText = getQueryStringValue(urlQuery.q, 'name', ApiQueryOperator.like);

  const onPageChange = (options: IApiQueryOptions) => setUrlQuery(options);

  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value.trim();
      setUrlQuery({ ...urlQuery, q: [{ key: 'name', operator: ApiQueryOperator.like, value: newValue }], offset: 0 });
    },
    [urlQuery, setUrlQuery]
  );
  const debounceOnSearchChange = useDebounce(onSearchChange, 800);

  const fetchAdminSiteStats = (queryParams: IApiQueryOptions) => {
    if (currentOrganizationId) {
      dispatch(getEmployerSitesRequest(currentOrganizationId, (response) => setDashboardSitesResponse(response), queryParams));
    }
  };

  useEffect(() => fetchAdminSiteStats(urlQuery), [currentOrganizationId, urlQuery]);

  return (
    <>
      <PageHeader>
        <PageTitle title={`Welcome back, ${firstName}`} subtitle={`${totalSites || '--'} Sites`} label="Dashboard" isLoading={isLoading} />
      </PageHeader>

      <Section>
        <SectionHeader isLoading={isLoading} title={`My Sites (${totalSites})`} data-cy="org-dashboard-content-header">
          {totalSites <= 0 && !defaultSearchText && !isLoading ? null : (
            <InputIconWrapper icon="Search" iconPosition="left">
              {isLoading ? <InputLoader width={190} /> : <Input height="large" placeholder="Search by site name" onChange={(e: React.ChangeEvent<HTMLInputElement>) => debounceOnSearchChange(e)} defaultValue={defaultSearchText} />}
            </InputIconWrapper>
          )}
        </SectionHeader>

        <PaginatedList total={totalSites} limit={urlQuery?.limit} offset={urlQuery?.offset} onPageChange={onPageChange}>
          <AdminSitesList sites={dashboardSites} isLoading={isLoading} limit={urlQuery?.limit} />
        </PaginatedList>
      </Section>
    </>
  );
};

export default AdminDashboard;
